import React, { useMemo } from "react";
import { format, addDays, parseISO } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { AlertTriangle, Image, Video, Link2, Calendar, MapPin, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface CreativeMetadata {
  url: string;
  locationName?: string;
  mediaOwner?: string;
  orientation?: "portrait" | "landscape";
  scheduledDays?: number[];
  resolution?: string;
}

interface CreativeScheduleTimelineProps {
  creativeUrls: string[];
  creativeMetadata?: CreativeMetadata[];
  startDate?: string;
  endDate?: string;
  language: "en" | "ar";
  selectedLocations?: Array<{ name?: string; mediaOwner?: string }>;
}

interface DaySlot {
  dayNumber: number;
  date: Date | null;
  creatives: Array<{
    url: string;
    index: number;
    metadata: CreativeMetadata;
  }>;
  hasConflict: boolean;
}

interface LocationSchedule {
  locationName: string;
  days: DaySlot[];
  totalCreatives: number;
}

// Helper to safely parse a date string
const parseDate = (raw: string | undefined): Date | null => {
  if (!raw) return null;
  const cleaned = String(raw).trim();
  if (!cleaned) return null;

  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(cleaned)
    ? `${cleaned}T12:00:00`
    : cleaned.includes(" ") && !cleaned.includes("T")
      ? cleaned.replace(" ", "T")
      : cleaned;

  try {
    const d = parseISO(normalized);
    if (Number.isFinite(d.getTime())) return d;
  } catch {
    // ignore
  }

  const fallback = new Date(cleaned);
  return Number.isFinite(fallback.getTime()) ? fallback : null;
};

// Get filename from URL for display
const getFilename = (url: string): string => {
  try {
    const decoded = decodeURIComponent(url);
    const filename = decoded.split("/").pop() || "";
    // Remove timestamp prefix if present
    return filename.replace(/^\d+-/, "").substring(0, 20) + (filename.length > 20 ? "..." : "");
  } catch {
    return url.substring(0, 20) + "...";
  }
};

// Get creative type icon
const getCreativeIcon = (url: string) => {
  const lower = url.toLowerCase();
  if (lower.includes("http") && !lower.match(/\.(jpg|jpeg|png|gif|mp4|mov|webm|avi)/i)) {
    return Link2;
  }
  if (lower.match(/\.(mp4|mov|webm|avi|mkv)/i)) {
    return Video;
  }
  return Image;
};

// Generate a color for each creative based on index
const getCreativeColor = (index: number): string => {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-orange-500",
    "bg-pink-500",
    "bg-cyan-500",
    "bg-yellow-500",
    "bg-indigo-500",
    "bg-red-500",
    "bg-teal-500",
  ];
  return colors[index % colors.length];
};

const getCreativeBorderColor = (index: number): string => {
  const colors = [
    "border-blue-500",
    "border-green-500",
    "border-purple-500",
    "border-orange-500",
    "border-pink-500",
    "border-cyan-500",
    "border-yellow-500",
    "border-indigo-500",
    "border-red-500",
    "border-teal-500",
  ];
  return colors[index % colors.length];
};

const CreativeScheduleTimeline: React.FC<CreativeScheduleTimelineProps> = ({
  creativeUrls,
  creativeMetadata = [],
  startDate,
  endDate,
  language,
  selectedLocations = [],
}) => {
  const locale = language === "ar" ? ar : enUS;
  const isRtl = language === "ar";

  // Calculate campaign days
  const campaignDays = useMemo(() => {
    const start = parseDate(startDate);
    const end = parseDate(endDate);

    if (!start || !end) return 0;

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return Math.min(diffDays, 365); // Cap at 365 days
  }, [startDate, endDate]);

  // Build location schedules
  const locationSchedules = useMemo((): LocationSchedule[] => {
    if (!creativeMetadata || creativeMetadata.length === 0) {
      return [];
    }

    // Group creatives by location
    const locationMap = new Map<string, Array<{ url: string; index: number; metadata: CreativeMetadata }>>();

    creativeMetadata.forEach((meta, idx) => {
      const locationName = meta.locationName || "Unassigned";
      if (!locationMap.has(locationName)) {
        locationMap.set(locationName, []);
      }
      locationMap.get(locationName)!.push({
        url: meta.url || creativeUrls[idx] || "",
        index: idx,
        metadata: meta,
      });
    });

    // Build schedule for each location
    const schedules: LocationSchedule[] = [];
    const baseDateParsed = parseDate(startDate);

    for (const [locationName, creatives] of locationMap.entries()) {
      const days: DaySlot[] = [];

      for (let dayNum = 1; dayNum <= campaignDays; dayNum++) {
        const creativesOnDay = creatives.filter((c) =>
          c.metadata.scheduledDays?.includes(dayNum)
        );

        days.push({
          dayNumber: dayNum,
          date: baseDateParsed ? addDays(baseDateParsed, dayNum - 1) : null,
          creatives: creativesOnDay,
          hasConflict: creativesOnDay.length > 1,
        });
      }

      schedules.push({
        locationName,
        days,
        totalCreatives: creatives.length,
      });
    }

    // Sort by location name
    return schedules.sort((a, b) => a.locationName.localeCompare(b.locationName));
  }, [creativeMetadata, creativeUrls, campaignDays, startDate]);

  // Check if any location has 2+ creatives - if not, don't render the timeline
  const hasMultipleCreativesPerLocation = locationSchedules.some(
    schedule => schedule.totalCreatives >= 2
  );

  // Count total conflicts
  const totalConflicts = useMemo(() => {
    return locationSchedules.reduce(
      (sum, loc) => sum + loc.days.filter((d) => d.hasConflict).length,
      0
    );
  }, [locationSchedules]);

  // Count empty days (no creative scheduled)
  const totalEmptyDays = useMemo(() => {
    return locationSchedules.reduce(
      (sum, loc) => sum + loc.days.filter((d) => d.creatives.length === 0).length,
      0
    );
  }, [locationSchedules]);

  // Don't show if no location has 2+ creatives (scheduling not needed)
  if (!hasMultipleCreativesPerLocation) {
    return null;
  }

  if (campaignDays === 0 || locationSchedules.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>{language === "ar" ? "لا توجد بيانات جدولة متاحة" : "No scheduling data available"}</p>
      </div>
    );
  }

  return (
    <Card className="border shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            {language === "ar" ? "جدول المحتوى حسب الموقع" : "Content Schedule by Location"}
          </CardTitle>
          <div className="flex items-center gap-3 text-sm">
            <Badge variant="outline" className="gap-1">
              {campaignDays} {language === "ar" ? "يوم" : "days"}
            </Badge>
            {totalConflicts > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="h-3 w-3" />
                {totalConflicts} {language === "ar" ? "تعارض" : "conflicts"}
              </Badge>
            )}
            {totalEmptyDays > 0 && (
              <Badge variant="secondary" className="gap-1 text-amber-600 bg-amber-50 border-amber-200">
                {totalEmptyDays} {language === "ar" ? "أيام فارغة" : "empty days"}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Legend */}
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-primary/20 border border-primary/40" />
              <span className="text-muted-foreground">
                {language === "ar" ? "محتوى مجدول" : "Scheduled Content"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-destructive/20 border-2 border-destructive" />
              <span className="text-muted-foreground">
                {language === "ar" ? "تعارض (محتوى متعدد)" : "Conflict (Multiple content)"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-muted border border-dashed border-muted-foreground/30" />
              <span className="text-muted-foreground">
                {language === "ar" ? "لا يوجد محتوى" : "No content"}
              </span>
            </div>
          </div>
        </div>

        {/* Timeline for each location */}
        <div className="space-y-6">
          {locationSchedules.map((location) => (
            <div key={location.locationName} className="space-y-2">
              {/* Location Header */}
              <div className="flex items-center gap-2 sticky left-0">
                <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="font-medium text-sm truncate max-w-[200px]" title={location.locationName}>
                  {location.locationName}
                </span>
                <Badge variant="secondary" className="text-xs">
                  {location.totalCreatives} {language === "ar" ? "ملف" : "file"}
                  {location.totalCreatives !== 1 && !isRtl ? "s" : ""}
                </Badge>
              </div>

              {/* Days Timeline */}
              <ScrollArea className="w-full">
                <div className="flex gap-1 min-w-max pb-2">
                  {location.days.map((day) => {
                    const hasContent = day.creatives.length > 0;
                    const hasConflict = day.hasConflict;

                    return (
                      <TooltipProvider key={day.dayNumber} delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className={`
                                flex-shrink-0 w-14 rounded-lg border p-1.5 text-center cursor-pointer
                                transition-all hover:scale-105 hover:shadow-md
                                ${hasConflict
                                  ? "bg-destructive/10 border-destructive border-2 animate-pulse"
                                  : hasContent
                                    ? "bg-primary/10 border-primary/40"
                                    : "bg-muted/30 border-dashed border-muted-foreground/20"
                                }
                              `}
                            >
                              {/* Day number */}
                              <div className="text-[10px] text-muted-foreground font-medium">
                                {language === "ar" ? `ي${day.dayNumber}` : `D${day.dayNumber}`}
                              </div>

                              {/* Date */}
                              {day.date && (
                                <div className="text-[9px] text-muted-foreground/70">
                                  {format(day.date, "d/M", { locale })}
                                </div>
                              )}

                              {/* Content indicator */}
                              <div className="mt-1 min-h-[20px] flex items-center justify-center">
                                {hasConflict ? (
                                  <div className="flex items-center gap-0.5">
                                    <AlertTriangle className="h-3 w-3 text-destructive" />
                                    <span className="text-[9px] font-bold text-destructive">
                                      {day.creatives.length}
                                    </span>
                                  </div>
                                ) : hasContent ? (
                                  <div className="flex items-center gap-0.5">
                                    {day.creatives.slice(0, 2).map((c, i) => {
                                      const Icon = getCreativeIcon(c.url);
                                      return (
                                        <div
                                          key={i}
                                          className={`h-4 w-4 rounded-full flex items-center justify-center ${getCreativeColor(c.index)}`}
                                        >
                                          <Icon className="h-2.5 w-2.5 text-white" />
                                        </div>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground/40">—</span>
                                )}
                              </div>
                            </div>
                          </TooltipTrigger>

                          <TooltipContent
                            side="bottom"
                            className="max-w-[280px] p-3"
                          >
                            <div className="space-y-2">
                              <div className="font-semibold text-sm">
                                {language === "ar" ? `اليوم ${day.dayNumber}` : `Day ${day.dayNumber}`}
                                {day.date && (
                                  <span className="font-normal text-muted-foreground ml-1">
                                    ({format(day.date, "EEE, MMM d, yyyy", { locale })})
                                  </span>
                                )}
                              </div>

                              {hasConflict && (
                                <div className="flex items-center gap-1 text-destructive text-xs">
                                  <AlertTriangle className="h-3 w-3" />
                                  {language === "ar"
                                    ? `تعارض: ${day.creatives.length} ملفات في نفس اليوم!`
                                    : `Conflict: ${day.creatives.length} files on the same day!`}
                                </div>
                              )}

                              {hasContent ? (
                                <div className="space-y-1.5">
                                  {day.creatives.map((c, i) => {
                                    const Icon = getCreativeIcon(c.url);
                                    return (
                                      <div
                                        key={i}
                                        className={`flex items-center gap-2 p-1.5 rounded border-l-2 bg-muted/30 ${getCreativeBorderColor(c.index)}`}
                                      >
                                        <div
                                          className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${getCreativeColor(c.index)}`}
                                        >
                                          <Icon className="h-3 w-3 text-white" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs font-medium truncate">
                                            {getFilename(c.url)}
                                          </p>
                                          <p className="text-[10px] text-muted-foreground">
                                            {language === "ar" ? `ملف #${c.index + 1}` : `File #${c.index + 1}`}
                                          </p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-xs text-muted-foreground italic">
                                  {language === "ar" ? "لا يوجد محتوى مجدول" : "No content scheduled"}
                                </p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>
          ))}
        </div>

        {/* Summary Footer */}
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Info className="h-3 w-3" />
              {language === "ar"
                ? "مرر فوق أي يوم لرؤية التفاصيل"
                : "Hover over any day to see details"}
            </div>
            <div>
              {locationSchedules.length} {language === "ar" ? "مواقع" : "locations"} •{" "}
              {creativeMetadata.length} {language === "ar" ? "ملفات" : "files"}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CreativeScheduleTimeline;
